/**
 * useUsers — fetch and manage CMS users (admin only).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  getAllUsers,
  approveUser,
  rejectUser,
  deactivateUser,
  activateUser,
  updateUserPermissions,
} from '@/services/userService';
import { useAuth } from './useAuth';
import type { UserRole, UserPermissions } from '@/types';

export function useUsers() {
  const { cmsUser } = useAuth();
  const queryClient = useQueryClient();

  const usersQuery = useQuery({
    queryKey: ['users'],
    queryFn:  getAllUsers,
    enabled:  !!cmsUser && cmsUser.role === 'admin',
  });

  const adminRef = cmsUser
    ? { uid: cmsUser.uid, displayName: cmsUser.displayName, email: cmsUser.email }
    : null;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['users'] });

  const approveMutation = useMutation({
    mutationFn: ({ uid, role, permissions }: { uid: string; role: UserRole; permissions: UserPermissions }) =>
      approveUser(uid, adminRef!, role, permissions),
    onSuccess: () => { toast.success('User approved.'); invalidate(); },
    onError:   () => toast.error('Failed to approve user.'),
  });

  const rejectMutation = useMutation({
    mutationFn: (uid: string) => rejectUser(uid, adminRef!),
    onSuccess: () => { toast.success('User rejected.'); invalidate(); },
    onError:   () => toast.error('Failed to reject user.'),
  });

  const deactivateMutation = useMutation({
    mutationFn: (uid: string) => deactivateUser(uid, adminRef!),
    onSuccess: () => { toast.success('User deactivated.'); invalidate(); },
    onError:   () => toast.error('Failed to deactivate user.'),
  });

  const activateMutation = useMutation({
    mutationFn: (uid: string) => activateUser(uid, adminRef!),
    onSuccess: () => { toast.success('User activated.'); invalidate(); },
    onError:   () => toast.error('Failed to activate user.'),
  });

  const updatePermsMutation = useMutation({
    mutationFn: ({ uid, role, permissions }: { uid: string; role: UserRole; permissions: UserPermissions }) =>
      updateUserPermissions(uid, adminRef!, role, permissions),
    onSuccess: () => { toast.success('Permissions updated.'); invalidate(); },
    onError:   () => toast.error('Failed to update permissions.'),
  });

  return {
    users:      usersQuery.data ?? [],
    loading:    usersQuery.isLoading,
    error:      usersQuery.error,
    approve:    approveMutation.mutate,
    reject:     rejectMutation.mutate,
    deactivate: deactivateMutation.mutate,
    activate:   activateMutation.mutate,
    updatePerms:updatePermsMutation.mutate,
    isApproving:    approveMutation.isPending,
    isRejecting:    rejectMutation.isPending,
    isDeactivating: deactivateMutation.isPending,
    isActivating:   activateMutation.isPending,
    isUpdatingPerms:updatePermsMutation.isPending,
  };
}
